import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Search, X } from 'lucide-react';

import { useCountriesOptions } from '@/api/countries';
import { useEstateOptions } from '@/api/estate-options';
import { useMyIpLocation } from '@/api/me';
import { useMyTenants } from '@/api/tenant';
import { closeModal } from '@/components/modals/modals.utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MapBox } from '@/components/ui/map';
import type { SelectOption } from '@/components/ui/select';
import { Select } from '@/components/ui/select';
import Spinner from '@/components/ui/spinner';
import { PROPERTY_LEVELS } from '@/constants/property-level';
import { getTenantProjects } from '@/features/tenants/api/tenant-projects';
import { useDebouncedValue } from '@/hooks/use-debounced-value';

import { usePlaceSuggestions } from '../api/place-suggestions';
import type { EstateSchemaType } from '../schemas/estate';
import type { OnSelectSuggestion } from './address-autocomplete';
import { AddressAutocomplete } from './address-autocomplete';

interface EstateFormProps {
  label: string;
  options?: {
    propertyLevelDisabled?: boolean;
    projectDisabled?: boolean;
    tenantDisabled?: boolean;
  };
  onCancel?: () => void;
  onSubmit: (data: EstateSchemaType) => Promise<void> | void;
}

export const EstateForm: React.FC<EstateFormProps> = ({
  label,
  options = {},
  onCancel,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const { data: countries } = useCountriesOptions();
  const { data } = useEstateOptions();
  const { data: tenants } = useMyTenants();
  const { data: ipLocation } = useMyIpLocation();

  const tenantOptions = useMemo(
    () =>
      tenants.map((tenant) => ({
        title: tenant.name,
        id: tenant.id,
      })),
    [tenants]
  );

  const { handleSubmit, watch, reset, control } = useFormContext<EstateSchemaType>();

  const addressData = watch('addressData');
  const propertyLevel = watch('estateData.propertyLevel');
  const tenantId = watch('estateData.tenantId');

  const [loading, setLoading] = useState(false);
  const [projectOptions, setProjectOptions] = useState<SelectOption[]>([]);

  const getFullAddress = useCallback(
    (addressData: Omit<EstateSchemaType['addressData'], 'address'>) => {
      const { street = '', houseNumber = '', postalCode = '', city = '', countryId } = addressData;
      return [
        `${street} ${houseNumber}`.trim(),
        `${postalCode} ${city}`.trim(),
        (countries || []).find((c: { id: string }) => c.id === countryId)?.title || '',
      ]
        .filter((val) => val.length)
        .join(', ');
    },
    [countries]
  );

  useEffect(() => {
    if (tenantId) {
      getTenantProjects(tenantId).then((projects) => {
        setProjectOptions(
          projects.map((project) => ({
            title: project.name || `Project_${project.id}`,
            id: project.id,
          }))
        );
      });
    }
  }, [tenantId]);

  const fullAddress = useMemo(() => {
    return getFullAddress({
      houseNumber: addressData.houseNumber,
      street: addressData.street,
      city: addressData.city,
      postalCode: addressData.postalCode,
      countryId: addressData.countryId,
    });
  }, [
    getFullAddress,
    addressData.street,
    addressData.houseNumber,
    addressData.postalCode,
    addressData.city,
    addressData.countryId,
  ]);

  const debouncedFullAddress = useDebouncedValue(fullAddress, 500);

  const { data: placeSuggestions } = usePlaceSuggestions(debouncedFullAddress);

  useEffect(() => {
    if (placeSuggestions?.length) {
      reset((prev) => ({
        ...prev,
        addressData: {
          ...prev.addressData,
          lat: placeSuggestions[0].latitude,
          long: placeSuggestions[0].longitude,
        },
      }));
    }
  }, [reset, placeSuggestions]);

  useEffect(() => {
    reset((prev) => ({
      ...prev,
      addressData: {
        ...prev.addressData,
        address: fullAddress,
        addressAutoComplete: fullAddress,
      },
    }));
  }, [reset, fullAddress]);

  const onSubmitHandle = handleSubmit(async (data) => {
    try {
      setLoading(true);
      await onSubmit({
        ...data,
        addressData: {
          ...data.addressData,
          address: getFullAddress(data.addressData),
        },
      });
    } finally {
      setLoading(false);
    }
    return closeModal();
  });

  const onSelectSuggestion: OnSelectSuggestion = ({
    houseNumber,
    city,
    street,
    address,
    latitude,
    longitude,
    countryId,
    postalCode,
    countryCode,
  }) => {
    reset((prev) => ({
      ...prev,
      addressData: {
        ...prev.addressData,
        ...(address && { addressAutoComplete: address }),
        street: street ?? '',
        houseNumber: houseNumber ?? '',
        city: city ?? '',
        postalCode: postalCode ?? '',
        ...(address && { address }),
        ...(countryId && { countryId }),
        ...(latitude && { lat: latitude }),
        ...(longitude && { long: longitude }),
        ...(countryCode && { countryCode }),
      },
    }));
  };
  const onCancelHandle = () => {
    onCancel?.();
    return closeModal();
  };

  return (
    <form
      className="relative flex w-screen max-w-screen-sm flex-col gap-2 overflow-auto"
      onSubmit={onSubmitHandle}
    >
      <X className="ml-auto cursor-pointer stroke-[4px] text-primary-light" onClick={closeModal} />
      {loading && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-primary-lighter/40">
          <Spinner className="size-12 [&_*]:stroke-primary-dark" />
        </div>
      )}
      <p className="text-center">{label}</p>
      <div className="no-scrollbar flex max-h-[34rem] flex-col gap-2 overflow-y-scroll">
        <div className="w-full rounded-md border border-background-lighter">
          <p className="w-full rounded-t-md bg-background-lighter px-10 py-2">{t('general')}</p>
          <div className="px-10 pb-8 pt-5">
            <Controller
              name="estateData.name"
              control={control}
              render={({ field, formState }) => (
                <Input
                  {...field}
                  size="lg"
                  label={t('name')}
                  errors={[formState.errors.estateData?.name?.message || '']}
                  autoFocus
                />
              )}
            />
            <div className="grid grid-cols-2 gap-10">
              <Controller
                name="estateData.propertyType"
                control={control}
                render={({ field, formState }) => (
                  <Select
                    {...field}
                    label={t('propertyType')}
                    options={data?.propertyType || []}
                    errors={[formState.errors.estateData?.propertyType?.message || '']}
                  />
                )}
              />
              <Controller
                name="estateData.marketType"
                control={control}
                render={({ field, formState }) => (
                  <Select
                    {...field}
                    label={t('marketingType')}
                    options={data?.buyType || []}
                    errors={[formState.errors.estateData?.marketType?.message || '']}
                  />
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-10">
              <Controller
                name="estateData.propertyLevel"
                control={control}
                render={({ field, formState }) => (
                  <Select
                    {...field}
                    label={`${t('propertyLevel')}*`}
                    options={[
                      {
                        title: t('project'),
                        id: PROPERTY_LEVELS.PROJECT,
                      },
                      {
                        title: t('Estate'),
                        id: PROPERTY_LEVELS.OBJECT,
                      },
                    ]}
                    errors={[formState.errors.estateData?.propertyLevel?.message || '']}
                    onChange={(value) => {
                      field.onChange(value);
                      reset((prev) => ({
                        ...prev,
                        estateData: {
                          ...prev.estateData,
                          projectId: undefined,
                        },
                      }));
                    }}
                    disabled={options.propertyLevelDisabled}
                  />
                )}
              />

              {propertyLevel === PROPERTY_LEVELS.OBJECT ? (
                <Controller
                  name="estateData.projectId"
                  control={control}
                  render={({ field, formState }) => (
                    <Select
                      {...field}
                      label={t('project')}
                      options={projectOptions || []}
                      errors={[formState.errors.estateData?.projectId?.message || '']}
                      disabled={options.projectDisabled}
                    />
                  )}
                />
              ) : null}
            </div>

            {tenantOptions.length ? (
              <Controller
                name="estateData.tenantId"
                control={control}
                render={({ field, formState }) => (
                  <Select
                    {...field}
                    label={t('tenant')}
                    options={tenantOptions || []}
                    errors={[formState.errors.estateData?.tenantId?.message || '']}
                    disabled={options.tenantDisabled}
                  />
                )}
              />
            ) : null}
          </div>
        </div>

        <div className="z-50 w-full rounded-md border border-background-lighter">
          <p className="w-full rounded-t-md bg-background-lighter px-10 py-2">{t('address')}</p>
          <div className="px-10 pb-8 pt-5">
            <Controller
              name="addressData.addressAutoComplete"
              control={control}
              render={({ field, formState }) => (
                <AddressAutocomplete
                  {...field}
                  size="lg"
                  startIcon={<Search color="#9CA3AF" />}
                  label={t('address')}
                  onSelectSuggestion={onSelectSuggestion}
                  errors={[formState.errors.addressData?.addressAutoComplete?.message || '']}
                />
              )}
            />
            <div className="mt-4 size-full h-48 overflow-hidden rounded bg-black">
              <MapBox
                key={
                  addressData.lat ? `${addressData.lat}:${addressData.long}` : ipLocation?.latitude
                }
                fullscreenControl={false}
                zoom={addressData.long ? 12 : 4}
                {...(addressData.lat
                  ? {
                      latitude: Number(addressData.lat),
                      longitude: Number(addressData.long),
                    }
                  : ipLocation
                    ? {
                        latitude: ipLocation.latitude,
                        longitude: ipLocation.longitude,
                      }
                    : undefined)}
              />
            </div>
            <div className="mt-5 grid grid-cols-2 gap-x-10">
              <Controller
                name="addressData.houseNumber"
                control={control}
                render={({ field, formState }) => (
                  <Input
                    {...field}
                    size="lg"
                    label={t('houseNumber')}
                    errors={[formState.errors.addressData?.houseNumber?.message || '']}
                  />
                )}
              />
              <Controller
                name="addressData.street" // here must change
                control={control}
                render={({ field, formState }) => (
                  <Input
                    {...field}
                    size="lg"
                    label={t('street')}
                    errors={[formState.errors.addressData?.street?.message || '']}
                  />
                )}
              />
              <Controller
                name="addressData.city"
                control={control}
                render={({ field, formState }) => (
                  <Input
                    {...field}
                    size="lg"
                    label={t('city')}
                    errors={[formState.errors.addressData?.city?.message || '']}
                  />
                )}
              />
              <Controller
                name="addressData.countryId"
                control={control}
                render={({ field, formState }) => (
                  <Select
                    {...field}
                    size="lg"
                    label={t('country')}
                    options={countries || []}
                    errors={[formState.errors.addressData?.countryId?.message || '']}
                  />
                )}
              />
              <Controller
                name="addressData.postalCode"
                control={control}
                render={({ field, formState }) => (
                  <Input
                    {...field}
                    size="lg"
                    label={t('postalCode')}
                    errors={[formState.errors.addressData?.postalCode?.message || '']}
                  />
                )}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="mt-6 grid grid-cols-2 gap-1">
        <Button variant="outline" color="primary" onClick={onCancelHandle}>
          {t('cancel')}
        </Button>
        <Button type="submit">{t('submit')}</Button>
      </div>
    </form>
  );
};
